/**
 * /auth/google/callback — landing page of the auth service's Google flow.
 *
 * Google sends the user back here with `code` and `state`. The page hands
 * them to Friday's server, which exchanges the code on the auth service and
 * sets the session cookies. The page is reached by a top-level navigation, so
 * it must render on its own without the app shell.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useAuth } from '@/features/auth/authContext'
import { ApiError } from '@/lib/api'
import { FridayMark } from '@/components/branding/FridayMark'

export function GoogleCallbackPage() {
  const { completeGoogleSignIn } = useAuth()
  const navigate = useNavigate()
  const search = useSearch({ from: '/auth/google/callback' }) as {
    code?: string
    state?: string
    error?: string
  }
  const [error, setError] = useState<string | null>(null)
  // React runs effects twice in development and a Google code may only be
  // used once, so the exchange is started exactly once.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    if (search.error) {
      setError('Google sign-in was not completed. Please try again.')
      return
    }
    if (!search.code || !search.state) {
      setError('Google sign-in did not return a valid authorization code.')
      return
    }
    completeGoogleSignIn(search.code, search.state)
      .then((result) => navigate({ to: result.next }))
      .catch((e) => {
        setError(e instanceof ApiError ? e.message : 'Google sign-in could not be completed.')
      })
  }, [search.code, search.state, search.error, completeGoogleSignIn, navigate])

  return (
    <div className="flex h-full items-center justify-center bg-app px-4">
      <div className="w-full max-w-sm text-center">
        <FridayMark size={64} className="mx-auto mb-3" />
        {error ? (
          <>
            <h1 className="text-xl font-semibold tracking-tight">Sign-in failed</h1>
            <div className="mt-3 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
              {error}
            </div>
            <Link to="/login" search={{ next: undefined }} className="btn-primary mt-4 inline-flex justify-center">
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold tracking-tight">Signing you in…</h1>
            <p className="mt-1 text-sm text-fg-muted">Finishing your Google sign-in.</p>
          </>
        )}
      </div>
    </div>
  )
}
